import React, { useState } from "react";
import logo from "images/reskin/a-plus-logo.png";
import PropTypes from "prop-types";
import AccessBenefits from "../sell/AccessBenefits";
import PlanCardBenefitsButton from "../sell/PlanCardBenefitsButton";
import {
  ANNUAL,
  TEACHER_TIER,
  PLAN_SUBSCRIPTION_TYPES,
  PLAN_TYPES,
  A_PLUS_TIER
} from "../sell/constants";

const ButtonText = ({ isTeacher, isAPlus, isAnnualPlan, teacherFocus }) => {
  if (isTeacher && !teacherFocus) {
    return (
      <>
        Select A<sup>+</sup> Teacher
      </>
    );
  }
  if (isAPlus && !teacherFocus) {
    return (
      <>
        Select A<sup>+</sup>
      </>
    );
  }
  if (isAnnualPlan) {
    return <>Select Annual</>;
  }
  return <>Select Monthly</>;
};

const getAmplitudeText = (isTeacher, isAPlus, isAnnualPlan, teacherFocus) => {
  if (isTeacher && !teacherFocus) {
    return "Select A+ Teacher";
  }
  if (isAPlus && !teacherFocus) {
    return "Select A+";
  }
  if (isAnnualPlan) {
    return "Select Annual";
  }
  return "Select Monthly";
};

const getAmplitudeData = (isTeacher) => {
  return isTeacher ? "planPageCTAAplusTeacher" : "planPageCTAAplus";
};

const getButtonDataAttributes = (
  isTeacher,
  isAPlus,
  isAnnualPlan,
  teacherFocus
) => {
  return {
    "data-amplitude": getAmplitudeData(isTeacher),
    "data-amplitude-text": getAmplitudeText(
      isTeacher,
      isAPlus,
      isAnnualPlan,
      teacherFocus
    )
  };
};

const HeaderLogo = ({ isTeacher, isAPlus }) => {
  let logoJSX;

  if (isTeacher || isAPlus) {
    logoJSX = (
      <>
        <img src={logo} className="logo--teacher brand" alt="A+ logo." />
        {isTeacher ? (
          <span className="plan-card-text--teacher body-1">Teacher</span>
        ) : null}
      </>
    );
  } else {
    logoJSX = <span className="plan-card-text title-5">Free</span>;
  }

  return logoJSX;
};

const SellPlanCard = ({
  annualPrice,
  benefits,
  cents,
  dollars,
  groupInfoLink,
  loggedIn,
  planCode,
  planType,
  selectedPlan = ANNUAL,
  showGroupInfoLink = false,
  teacherFocus = false
}) => {
  const [showBenefits, setShowBenefits] = useState(teacherFocus);

  const isAnnualPlan = selectedPlan === ANNUAL;
  const isTeacher = planType === TEACHER_TIER;
  const isAPlus = planType === A_PLUS_TIER;
  const isPaid = isAPlus || isTeacher;

  const toggleBenefits = () => {
    setShowBenefits((prevState) => !prevState);
  };

  const renderSignUpButton = () => {
    return (
      <button
        type="button"
        className="btn btn-lg btn-a-plus btn-cta"
        data-toggle="modal"
        data-target="#sign-up-with-modal"
        {...getButtonDataAttributes(
          isTeacher,
          isAPlus,
          isAnnualPlan,
          teacherFocus
        )}
        data-plan-type={planType}
        data-subscription-type={planCode}
      >
        {renderButtonText()}
      </button>
    );
  };

  const renderPaymentPageLink = () => {
    return (
      <a
        href={`/sign-up/?subscription=${planCode}`}
        className="btn btn-lg btn-a-plus btn-cta"
        {...getButtonDataAttributes(
          isTeacher,
          isAPlus,
          isAnnualPlan,
          teacherFocus
        )}
      >
        {renderButtonText()}
      </a>
    );
  };

  const renderButtonText = () => {
    return (
      <ButtonText
        teacherFocus={teacherFocus}
        isTeacher={isTeacher}
        isAPlus={isAPlus}
        isAnnualPlan={isAnnualPlan}
      />
    );
  };

  return (
    <div className="lc-sell-plan-card-container">
      <div className="lc-sell-plan-card">
        <div className={`contents-container ${isPaid ? "" : "free-container"}`}>
          <div className="tw-flex tw-items-center">
            <HeaderLogo isTeacher={isTeacher} isAPlus={isAPlus} />
          </div>

          <div className="pricing-container">
            <div className="tw-inline-block">
              <span className="subscription-type__price--dollars">
                {dollars}
              </span>
              {cents && (
                <>
                  <sup className="subscription-type__price--cents">{cents}</sup>
                  <span className="currency body-1">USD/mo</span>
                </>
              )}
            </div>

            {isPaid ? (
              <>
                <div className="subcopy body-2">
                  {annualPrice && (
                    <span>Charged {annualPrice} USD every year</span>
                  )}
                  {!annualPrice && isTeacher && (
                    <span>Effortless prep, unbeatable value</span>
                  )}
                  {!annualPrice && !isTeacher && (
                    <span>A small price to pay for an &quot;A&quot;</span>
                  )}
                </div>
                {loggedIn ? renderPaymentPageLink() : renderSignUpButton()}
              </>
            ) : (
              <>
                <div className="subcopy body-2">
                  Basic features to get started
                </div>
                <button
                  type="button"
                  className="btn btn-lg btn-default btn-cta"
                  data-toggle="modal"
                  data-target="#sell-plan-free-card .modal"
                  data-subscription-type={ANNUAL}
                  data-amplitude="selectFreeSubscription"
                >
                  Get Started
                </button>
              </>
            )}
            <div className="tw-hidden sm:tw-block benefits-list-container">
              <AccessBenefits sellPageCardVariation benefits={benefits} />
            </div>
            {/* On mobile views, we only want to show the supported benefits for the 'Free' plan. */}
            {showBenefits ? (
              <div className="sm:tw-hidden benefits-list-container">
                <AccessBenefits sellPageCardVariation benefits={benefits} />
              </div>
            ) : null}
          </div>
        </div>
        <div className="sm:tw-hidden">
          <PlanCardBenefitsButton
            isPaid={isPaid}
            expanded={showBenefits}
            onToggle={toggleBenefits}
          />
        </div>
      </div>
      <div>
        {showGroupInfoLink && (
          <div className="tw-pb-[60px]">
            <div className="plan_cards__group-link">
              <span className="lc-icon lc-icon-related-quote lc-icon-md" />
              <a
                href={groupInfoLink}
                className="groups-promo__link"
                data-amplitude="learnAboutGroupSubscriptions"
              >
                Learn about group subscriptions
              </a>
            </div>
          </div>
        )}
        {!showGroupInfoLink && (
          <div className="empty-group-link tw-hidden sm:tw-block" />
        )}
      </div>
    </div>
  );
};

SellPlanCard.propTypes = {
  annualPrice: PropTypes.string,
  benefits: PropTypes.arrayOf(
    PropTypes.shape({
      img: PropTypes.string.isRequired,
      showOnly: PropTypes.arrayOf(PropTypes.oneOf(PLAN_SUBSCRIPTION_TYPES)),
      subcopy: PropTypes.arrayOf(
        PropTypes.shape({
          alignment: PropTypes.oneOf(["left", "center", "right"]).isRequired,
          text: PropTypes.string.isRequired
        })
      ).isRequired,
      text: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired
    })
  ).isRequired,
  cents: PropTypes.string,
  dollars: PropTypes.string.isRequired,
  groupInfoLink: PropTypes.string.isRequired,
  loggedIn: PropTypes.bool,
  planCode: PropTypes.string,
  planType: PropTypes.oneOf(PLAN_SUBSCRIPTION_TYPES).isRequired,
  selectedPlan: PropTypes.oneOf(PLAN_TYPES),
  showGroupInfoLink: PropTypes.bool,
  teacherFocus: PropTypes.bool
};

SellPlanCard.defaultProps = {
  annualPrice: null,
  cents: null,
  loggedIn: false,
  planCode: undefined,
  selectedPlan: ANNUAL,
  showGroupInfoLink: false,
  teacherFocus: false
};

export default SellPlanCard;
